import React, { useState, useCallback } from 'react';
import { getApiUrl } from '@/config/api';
import { UserCheck } from '@/utils/icons';

interface MatchEntry {
  external_id: string;
  external_name: string;
  first_name: string;
  last_name: string;
  provider: string;
  jersey: string;
  position: string;
  suggested_action: 'link' | 'create';
  suggested_user_id: number | null;
  suggested_user_name: string | null;
  suggested_user_email: string | null;
  confidence: number;
  match_type: 'exact_name' | 'fuzzy_name' | 'none';
}

interface LinkedEntry {
  external_id: string;
  external_name: string;
  linked_user_id: number | null;
  linked_user_name: string | null;
  linked_user_email: string | null;
}

interface TeamMember {
  id: number;
  name: string;
  email: string;
}

interface MatchPreviewResponse {
  provider: string;
  preview: MatchEntry[];
  already_linked: LinkedEntry[];
  total_athletes: number;
  team_id: number | null;
  team_name: string | null;
  team_members: TeamMember[];
}

interface Confirmation {
  external_id: string;
  action: 'link' | 'create';
  user_id?: number;
  email?: string;
  first_name: string;
  last_name: string;
}

interface PlayerMatchingModalProps {
  provider: 'hawkin' | 'catapult';
  onClose: () => void;
  onComplete: (results: any) => void;
  onNothingToReview?: () => void;
}

const PlayerMatchingModal: React.FC<PlayerMatchingModalProps> = ({
  provider,
  onClose,
  onComplete,
  onNothingToReview,
}) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [previewData, setPreviewData] = useState<MatchPreviewResponse | null>(null);
  const [decisions, setDecisions] = useState<Record<string, Confirmation>>({});
  const [emails, setEmails] = useState<Record<string, string>>({});
  const [pickingFor, setPickingFor] = useState<string | null>(null);

  const fetchPreview = useCallback(async () => {
    const access = localStorage.getItem('access');
    if (!access) return;

    setLoading(true);
    setError('');
    try {
      const res = await fetch(getApiUrl('/api/wearables/match-preview/'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${access}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ provider }),
      });
      const data: MatchPreviewResponse = await res.json();
      if (!res.ok) {
        setError((data as any).error || 'Failed to load preview');
        return;
      }
      setPreviewData(data);

      if (data.preview.length === 0) {
        if (onNothingToReview) {
          onNothingToReview();
        }
        return;
      }

      const initial: Record<string, Confirmation> = {};
      for (const entry of data.preview) {
        initial[entry.external_id] = {
          external_id: entry.external_id,
          action: entry.suggested_action,
          user_id: entry.suggested_user_id ?? undefined,
          first_name: entry.first_name,
          last_name: entry.last_name,
        };
      }
      setDecisions(initial);
    } catch (err: any) {
      setError(err.message || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  }, [provider]);

  React.useEffect(() => {
    fetchPreview();
  }, [fetchPreview]);

  const updateAction = (extId: string, action: 'link' | 'create') => {
    setDecisions((prev) => ({
      ...prev,
      [extId]: { ...prev[extId], action, user_id: action === 'create' ? undefined : prev[extId]?.user_id },
    }));
    setPickingFor(null);
  };

  const linkToUser = (extId: string, member: TeamMember) => {
    setDecisions((prev) => ({
      ...prev,
      [extId]: { ...prev[extId], action: 'link', user_id: member.id },
    }));
    setPickingFor(null);
  };

  const handleSubmit = async () => {
    const access = localStorage.getItem('access');
    if (!access) return;

    setSubmitting(true);
    setError('');

    const confirmations = Object.values(decisions).map((d) => ({
      ...d,
      email: emails[d.external_id] || undefined,
    }));

    try {
      const res = await fetch(getApiUrl('/api/wearables/confirm-matches/'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${access}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          provider,
          team_id: previewData?.team_id,
          confirmations,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError((data as any).error || 'Failed to confirm matches');
        return;
      }
      onComplete(data.results);
    } catch (err: any) {
      setError(err.message || 'Failed to confirm');
    } finally {
      setSubmitting(false);
    }
  };

  const preview = previewData?.preview || [];
  const alreadyLinked = previewData?.already_linked || [];
  const teamMembers = previewData?.team_members || [];
  const createCount = Object.values(decisions).filter((d) => d.action === 'create').length;
  const linkCount = Object.values(decisions).filter((d) => d.action === 'link').length;
  const totalFound = preview.length + alreadyLinked.length;
  const providerLabel = provider === 'hawkin' ? 'Hawkin Dynamics' : 'Catapult';

  const getLinkedMember = (extId: string): TeamMember | undefined => {
    const uid = decisions[extId]?.user_id;
    if (!uid) return undefined;
    return teamMembers.find((m) => m.id === uid);
  };

  const buildSummary = (): string => {
    if (!previewData || preview.length === 0) return '';
    const parts: string[] = [];
    if (linkCount > 0) {
      parts.push(`${linkCount} matched ${linkCount === 1 ? 'an existing account' : 'existing accounts'}`);
    }
    if (createCount > 0) {
      parts.push(`${createCount} will be created as new ${createCount === 1 ? 'user' : 'users'}`);
    }
    return parts.join(' and ') + '.';
  };

  return (
    <div className="cgm-modal-backdrop">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="cgm-card cgm-modal-panel p-0 max-w-lg w-full max-h-[85vh] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="text-center px-6 pt-6 pb-4">
            <h3 className="text-xl cgm-text-primary mb-1.5">
              {loading
                ? `Connecting to ${providerLabel}…`
                : `${providerLabel} Found ${totalFound} Player${totalFound !== 1 ? 's' : ''}`}
            </h3>
            {!loading && preview.length > 0 && (
              <p className="cgm-text-secondary text-sm">
                {buildSummary()}
                {' '}Review below and adjust if needed.
                {createCount > 0 && ' Add an email to send an invite — or leave blank and add it later.'}
              </p>
            )}
            {!loading && preview.length === 0 && (
              <p className="cgm-text-secondary text-sm">
                All players are already linked to accounts.
              </p>
            )}
          </div>

          {error && (
            <div className="mx-6 mb-3">
              <div className="cgm-alert-error px-3 py-2 rounded-md text-sm">{error}</div>
            </div>
          )}

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6">
            {loading && (
              <div className="flex flex-col items-center justify-center py-16 cgm-text-secondary">
                <div className="animate-spin rounded-full h-6 w-6 mb-3 cgm-spinner border-2" />
                <span className="text-sm">Analyzing players…</span>
              </div>
            )}

            {!loading && preview.length === 0 && !error && (
              <div className="text-center py-8 cgm-text-muted text-sm">
                Nothing to do — you're all set.
              </div>
            )}

            {/* Player list */}
            {!loading && preview.length > 0 && (
              <ul className="divide-y" style={{ borderColor: 'var(--card-border)' }}>
                {preview.map((entry) => {
                  const decision = decisions[entry.external_id];
                  const isLink = decision?.action === 'link';
                  const manualMember = getLinkedMember(entry.external_id);
                  const isPicking = pickingFor === entry.external_id;
                  const displayName = isLink
                    ? (manualMember?.name || entry.suggested_user_name)
                    : null;
                  const displayEmail = isLink
                    ? (manualMember?.email || entry.suggested_user_email)
                    : null;

                  return (
                    <li
                      key={entry.external_id}
                      className="flex items-center gap-2 py-2.5"
                    >
                      {isLink ? (
                        <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400 w-14">
                          Matched
                        </span>
                      ) : (
                        <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400 w-14">
                          New
                        </span>
                      )}

                      <span className="cgm-text-primary text-sm font-medium truncate w-32 shrink-0">
                        {entry.external_name || 'Unknown'}
                      </span>

                      <div className="flex-1 min-w-0">
                        {isLink && !isPicking ? (
                          <span className="text-sm cgm-text-secondary truncate block">
                            → {displayName}
                            {displayEmail && (
                              <span className="cgm-text-muted ml-1">
                                ({displayEmail})
                              </span>
                            )}
                          </span>
                        ) : isPicking ? (
                          <select
                            autoFocus
                            className="cgm-input w-full text-sm px-3 py-1.5"
                            defaultValue=""
                            onChange={(e) => {
                              const member = teamMembers.find((m) => m.id === Number(e.target.value));
                              if (member) linkToUser(entry.external_id, member);
                            }}
                          >
                            <option value="" disabled>Select a player…</option>
                            {teamMembers.map((m) => (
                              <option key={m.id} value={m.id}>{m.name}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="email"
                            placeholder="Email (optional)"
                            value={emails[entry.external_id] || ''}
                            onChange={(e) =>
                              setEmails((prev) => ({
                                ...prev,
                                [entry.external_id]: e.target.value,
                              }))
                            }
                            className="cgm-input w-full text-sm px-3 py-1.5"
                          />
                        )}
                      </div>

                      {teamMembers.length > 0 && (
                        <button
                          onClick={() => {
                            if (isLink) {
                              updateAction(entry.external_id, 'create');
                            } else if (isPicking) {
                              setPickingFor(null);
                            } else {
                              setPickingFor(entry.external_id);
                            }
                          }}
                          className="shrink-0 text-xs font-medium cgm-text-muted hover:cgm-text-primary transition-colors px-2 py-1 underline decoration-dotted underline-offset-2"
                        >
                          {isLink ? 'Unlink' : isPicking ? 'New' : 'Link'}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            {/* Already linked section */}
            {!loading && alreadyLinked.length > 0 && (
              <div className="mt-5 mb-2">
                <label className="block text-sm font-medium cgm-text-secondary mb-2">
                  Already Linked ({alreadyLinked.length})
                </label>
                <ul className="divide-y" style={{ borderColor: 'var(--card-border)' }}>
                  {alreadyLinked.map((linked) => (
                    <li
                      key={linked.external_id}
                      className="flex items-center gap-2 py-2.5 opacity-50"
                    >
                      <UserCheck className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                      <span className="cgm-text-primary text-sm">{linked.external_name}</span>
                      <span className="text-sm cgm-text-muted">
                        → {linked.linked_user_name || 'Unknown user'}
                        {linked.linked_user_email && (
                          <span className="ml-1">({linked.linked_user_email})</span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Footer */}
          {!loading && preview.length > 0 && (
            <div className="px-6 pt-3 pb-6 space-y-2">
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="cgm-button w-full py-3 px-4 flex items-center justify-center"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 mr-3 cgm-spinner border-2" />
                    Processing…
                  </>
                ) : (
                  `Confirm All ${preview.length} Player${preview.length !== 1 ? 's' : ''}`
                )}
              </button>
              <button
                onClick={onClose}
                className="cgm-cancel-button w-full py-3 px-4 flex items-center justify-center rounded-lg font-medium"
              >
                Cancel
              </button>
            </div>
          )}

          {/* Close-only footer when nothing to action */}
          {!loading && preview.length === 0 && !error && (
            <div className="px-6 pt-3 pb-6">
              <button
                onClick={onClose}
                className="cgm-button w-full py-3 px-4 flex items-center justify-center"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlayerMatchingModal;
